import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Swal from "sweetalert2";
import Breadcrums from "../../common/breadcrumbs";
import Loader from "../../common/loader";
import { formateDateWithMonth, handleServerValidations, globalLoader } from "../../../../utils/commonfunction";
import { SWAL_SETTINGS } from "../../../../utils/Constants";
import { UpdateStatus, GetDownloadLogs, RefundReportBooking } from "../../services/reportBooking.services";
import moment from "moment";

const ViewReportBooking = () => {
    const { t } = useTranslation();
    const location = useLocation();
    const navigate = useNavigate();
    
    const [booking, setBooking] = useState(location.state?.booking);
    const [downloadLogs, setDownloadLogs] = useState([]);
    const [logsLoading, setLogsLoading] = useState(false);

    const handleRefund = () => {
        if (!booking) return;
        const isCancelledOrRefunded = booking.booking_status === "cancelled" || booking.payment?.payment_status === "refunded" || booking.payment?.payment_status === "refund_initiated";
        if (isCancelledOrRefunded) {
            Swal.fire({
                icon: "info",
                text: "This booking is already refunded or cancelled.",
                ...SWAL_SETTINGS,
            });
            return;
        }

        const paymentMethodText = booking.payment?.payment_method ? booking.payment.payment_method.toUpperCase() : "Wallet/Razorpay";

        Swal.fire({
            customClass: "swal-wide",
            title: "Initiate Refund",
            html: `Are you sure you want to refund this booking?<br/><br/>
                   <b>User:</b> ${booking?.user?.first_name || ""} ${booking?.user?.last_name || ""} (${booking?.user?.email || "N/A"})<br/>
                   <b>Amount:</b> ${booking.currency_type || "INR"} ${booking.amount}<br/>
                   <b>Payment Method:</b> ${paymentMethodText}`,
            icon: "warning",
            showCancelButton: true,
            cancelButtonText: t("btn_cancel"),
            confirmButtonColor: "#dc3545",
            cancelButtonColor: "#6c757d",
            confirmButtonText: "Yes, Refund",
        }).then((result) => {
            if (result.isConfirmed) {
                globalLoader(true);
                RefundReportBooking({ id: booking.id })
                    .then((res) => {
                        if (res.status || res.success) {
                            Swal.fire({
                                icon: "success",
                                text: res.message || "Refund processed successfully!",
                                ...SWAL_SETTINGS,
                            });
                            setBooking(prev => ({
                                ...prev,
                                booking_status: "cancelled",
                                payment: {
                                    ...(prev?.payment || {}),
                                    payment_status: "refunded"
                                }
                            }));
                        } else {
                            Swal.fire({
                                icon: "error",
                                text: handleServerValidations(res),
                                ...SWAL_SETTINGS,
                            });
                        }
                        globalLoader(false);
                    })
                    .catch((err) => {
                        console.error(err);
                        Swal.fire({
                            icon: "error",
                            text: err?.response?.data?.message || "Refund failed. Please try again.",
                            ...SWAL_SETTINGS,
                        });
                        globalLoader(false);
                    });
            }
        });
    };

    useEffect(() => {
        if (booking?.id) {
            setLogsLoading(true);
            GetDownloadLogs({ booking_id: booking.id })
                .then((res) => {
                    setDownloadLogs(res?.data || []);
                    setLogsLoading(false);
                })
                .catch((err) => {
                    console.error(err);
                    setLogsLoading(false);
                });
        }
    }, [booking?.id]);

    const handleStatusChange = (id, newStatus) => {
        Swal.fire({
            customClass: "swal-wide",
            title: t("msg_are_you_sure"),
            text: `Change booking status to "${newStatus}"?`,
            icon: "warning",
            showCancelButton: true,
            cancelButtonText: t("btn_cancel"),
            confirmButtonColor: "#403fad",
            cancelButtonColor: "#f1388b",
            confirmButtonText: t("btn_yes"),
        }).then((result) => {
            if (result.isConfirmed) {
                const formData = new FormData();
                formData.append("id", id);
                formData.append("booking_status", newStatus);

                globalLoader(true);
                UpdateStatus(formData)
                    .then((res) => {
                        if (res.status || res.success) {
                            Swal.fire({
                                icon: "success",
                                text: res.message || "Status updated successfully!",
                                ...SWAL_SETTINGS,
                            });
                            setBooking(prev => ({ ...prev, booking_status: newStatus }));
                        } else {
                            Swal.fire({
                                icon: "error",
                                text: handleServerValidations(res),
                                ...SWAL_SETTINGS,
                            });
                        }
                        globalLoader(false);
                    })
                    .catch((err) => {
                        console.error(err);
                        Swal.fire({
                            icon: "error",
                            text: "Something went wrong. Please try again.",
                            ...SWAL_SETTINGS,
                        });
                        globalLoader(false);
                    });
            }
        });
    };

    const breadcrumbs = [
        { title: t("link_dashboard"), url: "/admin/dashboard" },
        { title: `${booking?.report_type || "Personalized"} Reports`, url: "/admin/report/booking-list/1" },
        { title: "View Booking", url: "" },
    ];

    if (!booking) {
        return (
            <div className="text-center mt-5">
                <h4>Booking details not found</h4>
                <button className="btn btn-primary mt-3" onClick={() => navigate(-1)}>Go Back</button>
            </div>
        );
    }

    const formData = booking.form_data || {};
    const hasFormData = Object.keys(formData).length > 0;

    const renderFormData = (dataObj) => {
        if (!dataObj || Object.keys(dataObj).length === 0) return <p>No data provided</p>;
        
        return (
            <table className="table table-bordered table-striped">
                <tbody>
                    {Object.entries(dataObj).map(([key, value]) => (
                        <tr key={key}>
                            <th style={{ width: "30%", textTransform: "capitalize" }}>
                                {key.replace(/_/g, " ")}
                            </th>
                            <td>{value?.toString() || "N/A"}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        );
    };

    return (
        <>
            <Breadcrums data={breadcrumbs} />
            <div className="animation_fade">
                <div className="card custom-card">
                    <div className="card-body">
                        <div className="d-flex justify-content-between align-items-center mb-4">
                            <h6 className="main-content-label">View {booking?.report_type || "Personalized"} Report Booking</h6>
                            <div className="d-flex gap-2">
                                <button
                                    className={`btn ${booking?.booking_status === 'cancelled' || booking?.payment?.payment_status === 'refunded' || booking?.payment?.payment_status === 'refund_initiated' ? 'btn-secondary' : 'btn-danger'}`}
                                    disabled={booking?.booking_status === 'cancelled' || booking?.payment?.payment_status === 'refunded' || booking?.payment?.payment_status === 'refund_initiated'}
                                    onClick={handleRefund}
                                >
                                    <i className="fa fa-undo me-1"></i> {booking?.booking_status === 'cancelled' || booking?.payment?.payment_status === 'refunded' || booking?.payment?.payment_status === 'refund_initiated' ? "Refunded" : "Refund Booking"}
                                </button>
                                {booking?.invoice_pdf && (
                                    <a
                                        href={booking.invoice_pdf}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="btn btn-primary"
                                        title="View Invoice PDF"
                                    >
                                        <i className="fa fa-file-pdf-o me-1"></i> View Invoice PDF
                                    </a>
                                )}
                                <button
                                    className="btn btn-secondary"
                                    onClick={() => navigate(-1)}
                                >
                                    <i className="fa fa-arrow-left me-1"></i> Back
                                </button>
                            </div>
                        </div>

                        <div className="row mb-4">
                            <div className="col-md-6">
                                <h6><strong>User Information</strong></h6>
                                <table className="table table-bordered">
                                    <tbody>
                                        <tr>
                                            <th>Name</th>
                                            <td>{booking?.user?.first_name} {booking?.user?.last_name}</td>
                                        </tr>
                                        <tr>
                                            <th>Email</th>
                                            <td>{booking?.user?.email}</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                            <div className="col-md-6">
                                <h6><strong>Booking Details</strong></h6>
                                <table className="table table-bordered">
                                    <tbody>
                                        <tr>
                                            <th>Order Number</th>
                                            <td><strong className="text-primary">{booking?.order_number || `ORD-REP-${booking?.id}`}</strong></td>
                                        </tr>
                                        <tr>
                                            <th>Report Title</th>
                                            <td>{booking?.report?.title || "N/A"}</td>
                                        </tr>
                                        <tr>
                                            <th>Amount Paid</th>
                                            <td>{booking?.currency_type} {booking?.amount}</td>
                                        </tr>
                                        {booking?.gst_percent !== undefined && (
                                            <>
                                                <tr>
                                                    <th>GST (%)</th>
                                                    <td>{booking?.gst_percent || 0}%</td>
                                                </tr>
                                                <tr>
                                                    <th>GST Amount</th>
                                                    <td>{booking?.currency_type} {booking?.gst_amount || 0}</td>
                                                </tr>
                                            </>
                                        )}
                                        <tr>
                                            <th>Booking Date</th>
                                            <td>{formateDateWithMonth(booking?.booking_date || booking?.createdAt)}</td>
                                        </tr>
                                        <tr>
                                            <th>Status</th>
                                            <td>
                                                {booking?.report_type !== "Instant" ? (
                                                    <div className="d-flex align-items-center gap-2">
                                                        <span className={`badge ${
                                                            booking.booking_status === 'completed' ? 'bg-success' :
                                                            booking.booking_status === 'in_progress' ? 'bg-primary' :
                                                            booking.booking_status === 'cancelled' ? 'bg-danger' :
                                                            booking.booking_status === 'confirmed' ? 'bg-info' :
                                                            'bg-warning'
                                                        }`}>
                                                            {booking.booking_status}
                                                        </span>
                                                    </div>
                                                ) : (
                                                    <span className={`badge ${booking.booking_status === 'completed' ? 'bg-success' : 'bg-warning'}`}>
                                                        {booking.booking_status}
                                                    </span>
                                                )}
                                            </td>
                                        </tr>
                                        <tr>
                                            <th>Report File / Link</th>
                                            <td>
                                                {booking?.report_file ? (
                                                    <a href={booking.report_file} target="_blank" rel="noreferrer" className="btn btn-sm btn-info">
                                                        <i className="fa fa-download me-1"></i> View Uploaded File
                                                    </a>
                                                ) : booking?.url ? (
                                                    <a href={booking.url} target="_blank" rel="noreferrer" className="btn btn-sm btn-outline-info">
                                                        <i className="fa fa-external-link me-1"></i> View External Link
                                                    </a>
                                                ) : (
                                                    <span className="text-muted">Not Uploaded</span>
                                                )}
                                            </td>
                                        </tr>
                                        <tr>
                                            <th>Downloaded</th>
                                            <td>
                                                {booking?.is_downloaded || booking?.download_count > 0 ? (
                                                    <span className="badge bg-success">Yes</span>
                                                ) : (
                                                    <span className="badge bg-secondary">No</span>
                                                )}
                                            </td>
                                        </tr>
                                        <tr>
                                            <th>Total Downloads</th>
                                            <td><span className="badge bg-info">{booking?.download_count || 0} times</span></td>
                                        </tr>
                                        <tr>
                                            <th>Last Download IP</th>
                                            <td><code>{booking?.last_download_ip || "N/A"}</code></td>
                                        </tr>
                                        {booking?.last_downloaded_at && (
                                            <tr>
                                                <th>Last Downloaded At</th>
                                                <td>{formateDateWithMonth(booking?.last_downloaded_at)} {new Date(booking?.last_downloaded_at).toLocaleTimeString()}</td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        <hr />

                        <div className="row mt-4">
                            <div className="col-12">
                                <h6 className="main-content-label mb-3">Download & IP Audit History</h6>
                                <div className="table-responsive">
                                    <table className="table table-bordered table-striped text-nowrap">
                                        <thead className="table-light">
                                            <tr>
                                                <th>#</th>
                                                <th>IP Address</th>
                                                <th>Downloaded Date</th>
                                                <th>Downloaded Time</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {logsLoading ? (
                                                <tr>
                                                    <td colSpan="4" className="text-center py-3">
                                                        <Loader />
                                                    </td>
                                                </tr>
                                            ) : downloadLogs.length > 0 ? (
                                                downloadLogs.map((log, idx) => {
                                                    const logTime = log.downloaded_at || log.createdAt;
                                                    return (
                                                        <tr key={idx}>
                                                            <td>{idx + 1}</td>
                                                            <td><code>{log.ip_address || "N/A"}</code></td>
                                                            <td>{logTime ? moment(logTime).format("DD MMM YYYY") : "N/A"}</td>
                                                            <td>{logTime ? moment(logTime).format("hh:mm:ss A") : "N/A"}</td>
                                                        </tr>
                                                    );
                                                })
                                            ) : (
                                                <tr>
                                                    <td colSpan="4" className="text-center py-3 text-muted">
                                                        No IP download history logs recorded yet for this report booking.
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>

                        {booking?.report_type !== "Instant" && (
                            <>
                                <hr />
                                
                                <div className="row mt-4">
                                    <div className="col-12">
                                        <h6 className="main-content-label mb-3">Submitted Form Details</h6>
                                        {!hasFormData ? (
                                            <div className="alert alert-info">No dynamic form data was submitted for this booking.</div>
                                        ) : formData.first_form_data ? (
                                            <>
                                                <div className="mb-4">
                                                    <h6 className="text-primary">{formData.first_form_title || "First Form Details"}</h6>
                                                    {renderFormData(formData.first_form_data)}
                                                </div>

                                                {formData.form_type === "double" && formData.second_form_data && (
                                                    <div className="mb-4">
                                                        <h6 className="text-secondary">{formData.second_form_title || "Second Form Details"}</h6>
                                                        {renderFormData(formData.second_form_data)}
                                                    </div>
                                                )}
                                            </>
                                        ) : (
                                            <div className="mb-4">
                                                <h6 className="text-primary">Form Details</h6>
                                                {renderFormData(formData)}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </div>
        </>
    );
};

export default ViewReportBooking;
